/*
 * Назначение: одна строка списка моделей проекта на вкладке «Модели».
 * Функционал: приоритет (основная/резерв), имя модели, выбор провайдера для
 * этой модели, перемещение по приоритету, вкл/выкл, тест одного запроса и
 * удаление из списка. Результат теста показывается под строкой.
 */

import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Loader2,
  Play,
  X,
} from "lucide-react";

import type { AiProvider } from "../../../lib/admin-api";
import type { ModelConfigEntry } from "../../../lib/types";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";

import { ProviderSelect } from "./provider-select";

export interface TestResult {
  loading: boolean;
  ok?: boolean;
  reply?: string;
  error?: string;
  latency?: number;
}

export function ModelRow({
  entry,
  index,
  total,
  providers,
  test,
  onMove,
  onToggle,
  onProvider,
  onMaxTokens,
  onTest,
  onRemove,
}: {
  entry: ModelConfigEntry;
  index: number;
  total: number;
  providers: AiProvider[];
  test?: TestResult;
  onMove: (index: number, dir: -1 | 1) => void;
  onToggle: (index: number, active: boolean) => void;
  onProvider: (index: number, providerId: string) => void;
  onMaxTokens: (index: number, maxTokens: number) => void;
  onTest: (entry: ModelConfigEntry) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="font-mono">
            {index === 0 ? "Основная" : `Резерв №${index}`}
          </Badge>
          <span className="font-mono text-sm">{entry.model}</span>
          {!entry.active && <Badge variant="secondary">Выключена</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Провайдер</span>
            <ProviderSelect
              value={entry.provider_id}
              providers={providers}
              onChange={(id) => onProvider(index, id)}
              className="w-[190px]"
            />
          </div>
          <label className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
            Лимит ответа
            <Input
              type="number"
              min={0}
              max={65536}
              step={256}
              inputMode="numeric"
              placeholder="как в задаче"
              value={entry.max_tokens ? String(entry.max_tokens) : ""}
              onChange={(e) => onMaxTokens(index, Math.max(0, Math.min(65536, Number(e.target.value) || 0)))}
              className="h-8 w-[120px]"
              title="Максимум токенов ответа этой модели; пусто — как в «Параметрах генерации»"
            />
          </label>
          <Button
            variant="ghost"
            size="icon"
            disabled={index === 0}
            onClick={() => onMove(index, -1)}
            title="Выше по приоритету"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            disabled={index === total - 1}
            onClick={() => onMove(index, 1)}
            title="Ниже по приоритету"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Switch
              checked={entry.active}
              onCheckedChange={(v) => onToggle(index, Boolean(v))}
            />
            <span className="text-xs text-muted-foreground">Активна</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onTest(entry)}
            disabled={test?.loading}
          >
            {test?.loading ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <Play className="mr-1 h-3 w-3" />
            )}
            Тест
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRemove(index)}
            title="Убрать из списка"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {test && !test.loading && (
        <div
          className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs ${
            test.ok ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
          }`}
        >
          {test.ok ? (
            <CheckCircle2 className="h-3 w-3" />
          ) : (
            <AlertCircle className="h-3 w-3" />
          )}
          {test.ok
            ? `OK · ${test.latency} мс · "${(test.reply || "").slice(0, 80)}"`
            : `Ошибка: ${test.error}`}
        </div>
      )}
    </div>
  );
}
